import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  FileText,
  Users,
  Send,
  ArrowLeftRight,
  Scale,
  Lock,
  CheckCircle2,
  PenLine,
} from "lucide-react";

export type ContractLifecycleState =
  | "draft"
  | "internal_review"
  | "out_for_negotiation"
  | "counter_received"
  | "legal_review"
  | "locked"
  | "approved"
  | "signed";

export const LIFECYCLE_STATE_LABELS: Record<ContractLifecycleState, string> = {
  draft: "Draft",
  internal_review: "Internal Review",
  out_for_negotiation: "Out for Negotiation",
  counter_received: "Counter Received",
  legal_review: "Legal Review",
  locked: "Locked",
  approved: "Approved",
  signed: "Signed",
};

export const LIFECYCLE_STATE_COLORS: Record<ContractLifecycleState, string> = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  internal_review: "bg-blue-50 text-blue-700 border-blue-200",
  out_for_negotiation: "bg-orange-50 text-orange-700 border-orange-200",
  counter_received: "bg-amber-50 text-amber-700 border-amber-200",
  legal_review: "bg-purple-50 text-purple-700 border-purple-200",
  locked: "bg-red-50 text-red-700 border-red-200",
  approved: "bg-green-50 text-green-700 border-green-200",
  signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const LIFECYCLE_STATE_ICONS: Record<ContractLifecycleState, React.ComponentType<{ className?: string }>> = {
  draft: FileText,
  internal_review: Users,
  out_for_negotiation: Send,
  counter_received: ArrowLeftRight,
  legal_review: Scale,
  locked: Lock,
  approved: CheckCircle2,
  signed: PenLine,
};

import React from "react";

interface LifecycleStateBadgeProps {
  state: ContractLifecycleState | string;
  size?: "sm" | "md";
  className?: string;
}

export function LifecycleStateBadge({ state, size = "md", className }: LifecycleStateBadgeProps) {
  const normalized = (state ?? "draft") as ContractLifecycleState;
  const label = LIFECYCLE_STATE_LABELS[normalized] ?? normalized;
  const colorClass = LIFECYCLE_STATE_COLORS[normalized] ?? "bg-slate-100 text-slate-700 border-slate-200";
  const Icon = LIFECYCLE_STATE_ICONS[normalized] ?? FileText;

  return (
    <Badge
      variant="outline"
      className={cn(
        "flex items-center gap-1 font-medium border",
        colorClass,
        size === "sm" ? "text-xs px-1.5 py-0.5" : "text-xs px-2 py-1",
        className,
      )}
    >
      <Icon className={cn(size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3")} />
      {label}
    </Badge>
  );
}

export const LIFECYCLE_PIPELINE: ContractLifecycleState[] = [
  "draft",
  "internal_review",
  "legal_review",
  "out_for_negotiation",
  "counter_received",
  "locked",
  "approved",
  "signed",
];

interface LifecyclePipelineProps {
  currentState: ContractLifecycleState | string;
  className?: string;
}

export function LifecyclePipeline({ currentState, className }: LifecyclePipelineProps) {
  const normalized = (currentState ?? "draft") as ContractLifecycleState;
  const currentIndex = LIFECYCLE_PIPELINE.indexOf(normalized);

  return (
    <div className={cn("flex items-center gap-1 overflow-x-auto pb-1", className)}>
      {LIFECYCLE_PIPELINE.map((state, idx) => {
        const isActive = state === normalized;
        const isPast = idx < currentIndex && !["out_for_negotiation", "counter_received"].includes(state);
        const colorClass = isActive ? LIFECYCLE_STATE_COLORS[state] : isPast ? "bg-slate-50 text-slate-400 border-slate-100" : "bg-white text-slate-300 border-slate-100";
        const Icon = LIFECYCLE_STATE_ICONS[state];

        return (
          <React.Fragment key={state}>
            <div
              className={cn(
                "flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs whitespace-nowrap font-medium transition-all",
                colorClass,
              )}
            >
              <Icon className="h-2.5 w-2.5" />
              {LIFECYCLE_STATE_LABELS[state]}
            </div>
            {idx < LIFECYCLE_PIPELINE.length - 1 && (
              <div className={cn("h-px w-3 flex-shrink-0", idx < currentIndex ? "bg-slate-400" : "bg-slate-200")} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
